import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

import { CountryCode, SvbrgIntakeSupplierSource } from '@/generated/gql/graphql';

export const supplierContactSchema = z.object({
  supplier: z.object({
    internalId: z.string().min(1),
    externalReference: z.string().nullish(),
    isQuoted: z.boolean(),
    source: z.enum(SvbrgIntakeSupplierSource).nullish(),
    reason: z.string().nullish(),
    location: z.object({
      internalId: z.string(),
      city: z.string().nullable(),
      postalCode: z.string().nullable(),
      address: z.string().nullable(),
      countryCode: z.enum(CountryCode).nullable(),
    }),
    contact: z.object({
      internalId: z.string(),
      email: z.email(),
      firstName: z.string().min(1),
      lastName: z.string().min(1),
      language: z.string().transform((value) => value.toUpperCase()),
    }),
  }),
});

export const supplierContactSchemaLocationNullable = supplierContactSchema.extend({
  supplier: supplierContactSchema.shape.supplier.extend({
    location: supplierContactSchema.shape.supplier.shape.location.nullish(),
  }),
});

export const supplierContactSchemaContactLocationNullable = supplierContactSchema.extend({
  supplier: supplierContactSchema.shape.supplier.extend({
    location: supplierContactSchema.shape.supplier.shape.location.nullish(),
    contact: supplierContactSchema.shape.supplier.shape.contact.nullish(),
  }),
});

export const supplierContactSchemaWithName = supplierContactSchema.extend({
  supplier: supplierContactSchema.shape.supplier.extend({
    name: z.string().min(1),
    location: supplierContactSchema.shape.supplier.shape.location.nullish(),
  }),
});

export const batchSupplierContactSchema = z.object({
  suppliers: z.array(supplierContactSchemaWithName).min(1),
});

export type SupplierContact = z.infer<typeof supplierContactSchema>;
export type SupplierContactArray = z.infer<typeof supplierContactSchema>;
export type SupplierContactLocationNullable = z.infer<typeof supplierContactSchemaLocationNullable>;
export type SupplierContactWithName = z.infer<typeof supplierContactSchemaWithName>;

export type BatchSupplierContact = z.infer<typeof batchSupplierContactSchema>;

export const supplierContactDataSchema = supplierContactSchema.extend({
  id: z.string().optional(),
});

export type SupplierContactData = z.infer<typeof supplierContactDataSchema>;

export const supplierContactDataSchemaContactLocationNullable = supplierContactSchemaContactLocationNullable.extend({
  id: z.string().optional(),
});

export type SupplierContactDataOptional = z.infer<typeof supplierContactDataSchemaContactLocationNullable>;

export const makeSupplierContactConfirmationItemSchema = (optional: boolean) =>
  supplierContactDataSchemaContactLocationNullable.superRefine((value, ctx) => {
    if (optional) {
      return;
    }
    if (value.supplier.contact == null) {
      ctx.addIssue({ code: 'custom', path: ['supplier', 'contact'], message: 'required' });
    }
    if (value.supplier.location == null) {
      ctx.addIssue({ code: 'custom', path: ['supplier', 'location'], message: 'required' });
    }
  });

export const makeSupplierContactConfirmationArraySchema = (optional: boolean) =>
  z.array(makeSupplierContactConfirmationItemSchema(optional)).min(1).max(1);

export const supplierContactDefault: DefaultValues<SupplierContact> = {
  supplier: {
    contact: {
      internalId: '',
      language: 'de',
      email: '',
      firstName: '',
      lastName: '',
    },
    isQuoted: false,
    source: null,
    location: {
      internalId: '',
    },
  },
};
export const supplierContactWithNameDefault: SupplierContactWithName = {
  supplier: {
    name: '',
    internalId: '',
    contact: {
      internalId: '',
      language: 'de',
      email: '',
      firstName: '',
      lastName: '',
    },
    isQuoted: false,
    source: null,
  },
};

export const supplierContactDataDefault: DefaultValues<SupplierContactData> = {
  id: '',
  ...supplierContactDefault,
};

export const supplierContactSelectionArraySchema = z.array(supplierContactDataSchema).min(0);
export const supplierContactConfirmationArraySchema = z.array(supplierContactDataSchema).min(1).max(1);
